import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Breadcrumbs from 'components/Breadcrumbs'
import DateFilter from 'components/DateFilter'
import LoadingIcon from 'components/LoadingIcon'
import OrderFilter from 'components/OrderFilter'
import Pagination from 'components/Pagination'
import SessionList from 'components/SessionList'
import { deleteSession, fetchSessions } from 'config/api'
import routes from 'config/routes'
import { useBankrolls } from 'hooks'
import React from 'react'
import { Button, Card, Col, Row } from 'react-bootstrap'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Link, Route, Switch, useLocation } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import { Session } from 'screens'
import { sleep } from 'utils'
import BankrollFilter from 'components/BankrollFilter'
import PlayerFilter from 'components/PlayerFilter'

const DEFAULT_PAGE = 1
const DEFAULT_PER_PAGE = 10

function Sessions() {
  const {
    currentBankroll,
    bankrolls,
    selectBankroll,
    createBankroll,
    players,
  } = useBankrolls()
  const { addToast } = useToasts()
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const page = parseInt(params.get('page')) || DEFAULT_PAGE
  const perPage = parseInt(params.get('per_page')) || DEFAULT_PER_PAGE
  const [orderFilter, setOrderFilter] = React.useState(null)
  const [playerFilter, setPlayerFilter] = React.useState(null)
  const [dateFilters, setDateFilters] = React.useState({
    date__gte: null,
    date__lte: null,
  })
  const qClient = useQueryClient()

  const qSessions = useQuery(
    ['sessions', { page, orderFilter, playerFilter, dateFilters }],
    async ({ queryKey }) => {
      const [, { page, orderFilter, playerFilter, dateFilters }] = queryKey
      // Minimum load time is .5 seconds so the loading icon doesn't
      // flash too quickly.
      await sleep(500)
      return fetchSessions(currentBankroll.id, {
        ordering: orderFilter,
        player: playerFilter,
        page,
        ...dateFilters,
      })
    },
    {
      onError: err => {
        addToast('Error retrieving sessions', {
          appearance: 'error',
          autoDismiss: true,
        })
        console.error('Error retrieving sessions')
        console.error(err)
      },
    },
  )

  const mSessions = useMutation(
    id => {
      return deleteSession(currentBankroll.id, id)
    },
    {
      onSuccess: () => {
        qClient.invalidateQueries('bankrolls')
        qClient.invalidateQueries('sessions')
      },
    },
  )

  const _deleteSession = async id => {
    try {
      await mSessions.mutateAsync(id)
      addToast('Session deleted', { autoDismiss: true, appearance: 'success' })
    } catch (err) {
      addToast(err.message, { autoDismiss: true, appearance: 'error' })
      console.error('Error deleting session')
      console.error(err)
    }
  }

  const handleDateFilter = React.useCallback((start, end) => {
    setDateFilters({
      date__gte: start,
      date__lte: end,
    })
  }, [])

  const handlePlayerFilter = React.useCallback(setPlayerFilter, [])

  const handleOrderFilter = order => {
    // When ordering we always want to sort by the most recent date
    // after the initial field is specified.
    setOrderFilter(order && `${order},-date`)
  }

  return (
    <Switch>
      <Route exact path={routes.sessionNew} component={Session} />
      <Route exact path={routes.sessionEdit} component={Session} />
      <Route>
        <div id="sessions">
          <Breadcrumbs
            crumbs={[
              ['DASHBOARD', routes.dashboard],
              ['SESSIONS', routes.sessions],
            ]}
          />
          <Row>
            <Col className="d-flex flex-wrap justify-content-start justify-content-md-between">
              <BankrollFilter
                current={currentBankroll}
                choices={bankrolls}
                onChange={selectBankroll}
                onCreate={createBankroll}
              />
              <div>
                <DateFilter
                  disabled={qSessions.isLoading}
                  onChange={handleDateFilter}
                />
                {currentBankroll.shared_users.length > 0 && (
                  <PlayerFilter
                    disabled={qSessions.isLoading}
                    onChange={handlePlayerFilter}
                    choices={players}
                  />
                )}
                <OrderFilter
                  disabled={qSessions.isLoading}
                  onChange={handleOrderFilter}
                  choices={[
                    ['Most Recent', null],
                    ['Earnings (Hi-Lo)', '-earnings'],
                    ['Earnings (Lo-Hi)', 'earnings'],
                    ['Location (A-Z)', 'location'],
                    ['Location (Z-A)', '-location'],
                  ]}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col className="mt-3">
              <Card className="shadow">
                <Card.Body>
                  <div className="mb-2 clearfix">
                    {qSessions.isSuccess &&
                    qSessions.data.results.length > 0 ? (
                      <Button
                        variant="warning"
                        size="sm"
                        as={Link}
                        to={routes.sessionNew}
                        className="mb-2 float-right"
                      >
                        <FontAwesomeIcon icon={['far', 'plus']} fixedWidth />
                        ADD SESSION
                      </Button>
                    ) : null}
                    <Card.Title>SESSIONS</Card.Title>
                  </div>
                  {qSessions.isLoading ? (
                    <LoadingIcon />
                  ) : qSessions.isError ? (
                    <p className="my-5 text-center font-weight-bold">
                      Unable to retreive sessions
                    </p>
                  ) : qSessions.data.results.length > 0 ? (
                    <>
                      <SessionList
                        sessions={qSessions.data.results}
                        onDelete={_deleteSession}
                      />
                      <Pagination
                        count={qSessions.data.count}
                        page={page}
                        perPage={perPage}
                      />
                    </>
                  ) : dateFilters.date__gte !== null ||
                    dateFilters.date__lte !== null ||
                    orderFilter !== null ||
                    playerFilter !== null ? (
                    <div className="my-5 text-center">NO SESSIONS FOUND</div>
                  ) : (
                    <div className="my-5 text-center">
                      <Button
                        variant="warning"
                        as={Link}
                        to={routes.sessionNew}
                      >
                        <FontAwesomeIcon icon={['far', 'plus']} fixedWidth />
                        ADD SESSION
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </Route>
    </Switch>
  )
}

export default Sessions
